// src/contexts/ContractContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { WalletContext } from './WalletContext';
import { setupContractListeners, cleanupContractListeners } from '../utils/contractUtils';

export const ContractContext = createContext();

export const ContractProvider = ({ children }) => {
  const { account, contract, web3, refreshBalance } = useContext(WalletContext);
  
  const [activePools, setActivePools] = useState([]);
  const [creatorPools, setCreatorPools] = useState([]);
  const [userEntries, setUserEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Store contract event listeners for cleanup
  const [contractListeners, setContractListeners] = useState(null);

  // Load all pools data
  const loadAllPoolsData = useCallback(async () => {
    if (!contract || !account) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        loadActivePools(),
        loadCreatorPools(),
        loadUserEntries()
      ]);
    } catch (err) {
      console.error('Failed to load pools data:', err);
      setError('Failed to load pools data: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [contract, account]);

// Helper function to pause execution
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Modified pool loading with rate limiting
const loadActivePools = useCallback(async () => {
  if (!contract) return;
  
  try {
    const poolCount = await contract.methods.poolCount().call();
    const pools = [];
    
    for (let i = 0; i < poolCount; i++) {
      try {
        // Add delay between requests to avoid rate limiting
        if (i > 0) await sleep(300);
        
        const poolInfo = await contract.methods.getPoolInfo(i).call();
        
        // Skip completed pools
        if (poolInfo[7]) continue;
        
        pools.push({
          id: i,
          creator: poolInfo[0],
          entryFee: web3.utils.fromWei(poolInfo[1], 'ether'),
          maxEntriesPerUser: poolInfo[2],
          maxEntries: poolInfo[3],
          totalEntries: poolInfo[4],
          creationTime: poolInfo[5],
          duration: poolInfo[6],
          completed: poolInfo[7],
          creatorFeePercent: poolInfo[8],
          platformFeePercent: poolInfo[9],
          gasFeePercent: poolInfo[10]
        });
      } catch (err) {
        console.error(`Error loading pool ${i}:`, err);
      }
    }
    
    setActivePools(pools);
    return pools;
  } catch (err) {
    console.error("Error loading pools:", err);
    setActivePools([]);
    return [];
  }
}, [contract, web3]);

  // Load pools created by the user
  const loadCreatorPools = useCallback(async () => {
    if (!contract || !account) return;
    
    try {
      const poolCount = await contract.methods.poolCount().call();
      const pools = [];
      
      for (let i = 0; i < poolCount; i++) {
        try {
          const poolInfo = await contract.methods.getPoolInfo(i).call();
          
          // Only include pools where the user is the creator
          if (poolInfo[0].toLowerCase() === account.toLowerCase()) {
            pools.push({
              id: i,
              creator: poolInfo[0],
              entryFee: web3.utils.fromWei(poolInfo[1], 'ether'),
              maxEntriesPerUser: poolInfo[2],
              maxEntries: poolInfo[3],
              totalEntries: poolInfo[4],
              creationTime: poolInfo[5],
              duration: poolInfo[6],
              completed: poolInfo[7],
              creatorFeePercent: poolInfo[8],
              platformFeePercent: poolInfo[9],
              gasFeePercent: poolInfo[10]
            });
          }
        } catch (err) {
          console.error(`Error loading pool ${i}:`, err);
          // Continue with next pool
        }
      }
      
      setCreatorPools(pools);
      return pools;
    } catch (err) {
      console.error("Error loading creator pools:", err);
      setError('Failed to load creator pools: ' + err.message);
      setCreatorPools([]);
      return [];
    }
  }, [contract, account, web3]);

  // Load user entries
  const loadUserEntries = useCallback(async () => {
    if (!contract || !account) return;
    
    try {
      const poolCount = await contract.methods.poolCount().call();
      const entries = [];
      
      for (let i = 0; i < poolCount; i++) {
        try {
          // Añadir retraso de 300ms entre solicitudes
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
          
          const entriesCount = await contract.methods.getUserEntries(i, account).call();
          
          if (parseInt(entriesCount) > 0) {
            // Otro retraso antes de obtener poolInfo
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const poolInfo = await contract.methods.getPoolInfo(i).call();
            
            entries.push({
              id: i,
              entriesCount,
              creator: poolInfo[0],
              entryFee: web3.utils.fromWei(poolInfo[1], 'ether'),
              // resto de propiedades...
            });
          }
        } catch (err) {
          console.error(`Error loading entries for pool ${i}:`, err);
        }
      }
      
      setUserEntries(entries);
      return entries;
    } catch (err) {
      console.error("Error loading user entries:", err);
      setUserEntries([]);
      return [];
    }
  }, [contract, account, web3]);

  // Create a new pool
  const createPool = async (poolData) => {
    if (!contract || !account) {
      throw new Error('Wallet not connected');
    }
    
    try {
      const { entryFee, maxEntriesPerUser, maxEntries, duration, creatorFee, platformFee, gasFee } = poolData;
      
      const entryFeeWei = web3.utils.toWei(entryFee.toString(), 'ether');
      const durationSeconds = duration * 3600; // Convert hours to seconds
      
      const tx = await contract.methods.createPool(
        entryFeeWei,
        maxEntriesPerUser,
        maxEntries,
        durationSeconds,
        creatorFee,
        platformFee,
        gasFee
      ).send({ from: account });
      
      // Reload data after creating pool
      await loadAllPoolsData();
      
      return tx;
    } catch (err) {
      console.error('Error creating pool:', err);
      throw err;
    }
  };

  // Enter a pool
  const enterPool = async (poolId, entriesCount) => {
    if (!contract || !account) {
      throw new Error('Wallet not connected');
    }
    
    try {
      const poolInfo = await contract.methods.getPoolInfo(poolId).call();
      const entryFeeWei = poolInfo.entryFee;
      const totalCostWei = web3.utils.toBN(entryFeeWei).mul(web3.utils.toBN(entriesCount));
      
      const tx = await contract.methods.enterPool(poolId, entriesCount).send({
        from: account,
        value: totalCostWei.toString()
      });
      
      // Reload data after entering pool
      await loadAllPoolsData();
      refreshBalance();
      
      return tx;
    } catch (err) {
      console.error('Error entering pool:', err);
      throw err;
    }
  };

  // Complete a pool
  const completePool = async (poolId) => {
    if (!contract || !account) {
      throw new Error('Wallet not connected');
    }
    
    try {
      const tx = await contract.methods.completePool(poolId).send({ from: account });
      
      // Reload data after completing pool
      await loadAllPoolsData();
      refreshBalance();
      
      return tx;
    } catch (err) {
      console.error('Error completing pool:', err);
      throw err;
    }
  };

  // Get pool winners
  const getPoolWinners = async (poolId) => {
    if (!contract) {
      throw new Error('Contract not initialized');
    }
    
    try {
      // First verify if the pool is completed
      const poolInfo = await contract.methods.getPoolInfo(poolId).call();
      
      if (!poolInfo.completed) {
        throw new Error('Pool is not completed yet');
      }
      
      const result = await contract.methods.getPoolWinners(poolId).call();
      const winners = result[0]; // Addresses
      const prizes = result[1]; // Prizes
      
      if (!winners.length) {
        throw new Error('No winners found for this pool');
      }
      
      return winners.map((address, index) => ({
        address,
        prize: web3.utils.fromWei(prizes[index], 'ether'),
        isCurrentUser: address.toLowerCase() === account?.toLowerCase()
      }));
    } catch (err) {
      console.error('Error getting pool winners:', err);
      throw err;
    }
  };

  // Setup contract event listeners
  useEffect(() => {
    if (contract && account) {
      const listeners = setupContractListeners(contract, {
        onPoolCreated: () => {
          loadActivePools();
          loadCreatorPools();
        },
        onEntryPurchased: (event) => {
          loadActivePools();
          if (event.returnValues.participant === account) {
            loadUserEntries();
            refreshBalance();
          }
        },
        onPoolCompleted: () => {
          loadAllPoolsData();
          refreshBalance();
        }
      });
      
      setContractListeners(listeners);
      
      return () => {
        cleanupContractListeners(listeners);
      };
    }
  }, [contract, account, loadActivePools, loadCreatorPools, loadUserEntries, loadAllPoolsData, refreshBalance]);

  // Initial data load
  useEffect(() => {
    if (contract && account) {
      loadAllPoolsData();
    }
  }, [contract, account, loadAllPoolsData]);

  return (
    <ContractContext.Provider
      value={{
        activePools,
        creatorPools,
        userEntries,
        isLoading,
        error,
        loadActivePools,
        loadCreatorPools,
        loadUserEntries,
        loadAllPoolsData,
        createPool,
        enterPool,
        completePool,
        getPoolWinners
      }}
    >
      {children}
    </ContractContext.Provider>
  );
}